import { PayloadAction } from '@reduxjs/toolkit'
import { initialState } from './state'
import {
  IntegrationPurchase, IntegrationStatics,
  MainIntegrationChart, MyIntegrationsType,
  IntegrationProjects,
  DeveloperSliceType,
  SmallProjectIntegration, CreditCardOrders, OffsetOptionalFieldsType, SmallWhiteLabel, FinalUnitPrice
} from 'src/core/models'

const reducers = {

  /* My Integration Reducers */
  setMyIntegrationLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.myIntegration.loading = action.payload
  },
  setMyIntegrationResponse: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.myIntegration.list = action.payload
  },
  setMyIntegrationError: (state: DeveloperSliceType, action: PayloadAction<string>) => {
    state.myIntegration.error = action.payload
  },

  /* User Integration List */
  setCreateIntegrationLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.createIntegration.loading = action.payload
  },
  setCreateIntegrationSuccess: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.createIntegration.success = action.payload
  },
  setCreateIntegrationError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.createIntegration.error = action.payload
  },
  resetCreateIntegrationState: (state: DeveloperSliceType) => {
    state.createIntegration = initialState.createIntegration
  },

  /* Create Integration Reducers */
  setUserIntegrationListLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.userIntegrationsList.loading = action.payload
  },
  setUserIntegrationListData: (state: DeveloperSliceType, action: PayloadAction<MyIntegrationsType[]>) => {
    state.userIntegrationsList.data = action.payload
  },
  setUserIntegrationListError: (state: DeveloperSliceType, action: PayloadAction<string>) => {
    state.userIntegrationsList.error = action.payload
  },
  setUserIntegrationsFiltered: (state: DeveloperSliceType, action: PayloadAction<MyIntegrationsType[]>) => {
    state.userIntegrationsList.filtered = action.payload
  },

  /* Main Integration Chart Reducers */
  setMainChartDataLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.mainChartData.loading = action.payload
  },
  setMainChartData: (state: DeveloperSliceType, action: PayloadAction<MainIntegrationChart>) => {
    const data = action.payload
    state.mainChartData.sum_amount = data.sum_amount
    state.mainChartData.compensated_amount = data.compensated_amount
    state.mainChartData.currency_symbol = data.currency_symbol
    state.mainChartData.data = data.data
  },

  /* Only One Integration Chart Reducers */
  setIntegrationChartLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationChartData.loading = action.payload
  },
  setIntegrationChartData: (state: DeveloperSliceType, action: PayloadAction<MainIntegrationChart>) => {
    const data = action.payload
    state.integrationChartData.sum_amount = data.sum_amount
    state.integrationChartData.compensated_amount = data.compensated_amount
    state.integrationChartData.currency_symbol = data.currency_symbol
    state.integrationChartData.data = data.data
  },

  /* Integration Purchases List Reducers */
  setIntegrationPurchasesListLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationPurchasesList.loading = action.payload
  },
  setIntegrationPurchasesListError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.integrationPurchasesList.loading = action.payload
  },
  setIntegrationPurchasesList: (state: DeveloperSliceType, action: PayloadAction<IntegrationPurchase[]>) => {
    state.integrationPurchasesList.data = action.payload
  },
  setIntegrationPurchasesFiltered: (state: DeveloperSliceType, action: PayloadAction<IntegrationPurchase[]>) => {
    state.integrationPurchasesList.filtered = action.payload
  },

  /* Integration Purchases List Reducers */
  setIntegrationStaticsLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationStatics.loading = action.payload
  },
  setIntegrationStaticsData: (state: DeveloperSliceType, action: PayloadAction<IntegrationStatics>) => {
    state.integrationStatics.data = action.payload
  },
  resetIntegrationStaticsState: (state: DeveloperSliceType) => {
    state.integrationStatics = initialState.integrationStatics
  },

  /* Integration Projects List Reducers */
  setIntegrationProjectsListLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationProjectsList.loading = action.payload
  },
  setIntegrationProjectsListError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.integrationProjectsList.error = action.payload
  },
  setIntegrationProjectsList: (state: DeveloperSliceType, action: PayloadAction<IntegrationProjects[]>) => {
    state.integrationProjectsList.data = action.payload
  },
  /* Integration To Share Projects List Reducers */
  setIntegrationToShareProjectsListLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationsToShareProjectList.loading = action.payload
  },
  setIntegrationToShareProjectsListError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.integrationsToShareProjectList.error = action.payload
  },
  setIntegrationToShareProjectsListData: (state: DeveloperSliceType, action: PayloadAction<MyIntegrationsType[]>) => {
    state.integrationsToShareProjectList.data = action.payload
  },
  setIntegrationToShareProjectsListProjectId: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.integrationsToShareProjectList.projectId = action.payload
  },
  setIntegrationToShareProjectsListFlag: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationsToShareProjectList.flag = action.payload
  },
  /* Deactivate Integration Reducers */
  setDeactivateIntegrationLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.deactivateIntegration.loading = action.payload
  },
  setDeactivateIntegrationError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.deactivateIntegration.error = action.payload
  },

  /* Reactivate Integration Reducers */
  setReactivateIntegrationLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.reactivateIntegration.loading = action.payload
  },
  setReactivateIntegrationError: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.reactivateIntegration.error = action.payload
  },
  setReactivateIntegrationSuccess: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.reactivateIntegration.success = action.payload
  },
  resetReactivateIntegrationState: (state: DeveloperSliceType) => {
    state.reactivateIntegration = initialState.reactivateIntegration
  },

  /* Integration WhiteLabel Reducers */
  setWhiteLabelDetailsLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.integrationWhiteLabel.loading = action.payload
  },
  setWhiteLabelDetailsData: (state: DeveloperSliceType, action: PayloadAction<SmallWhiteLabel>) => {
    state.integrationWhiteLabel.whiteLabel = action.payload
  },
  resetWhiteLabelDetails: (state: DeveloperSliceType) => {
    state.integrationWhiteLabel = initialState.integrationWhiteLabel
  },

  /* Update Logo Reducers */
  setUpdateLogoLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.updateLogo.loading = action.payload
  },
  setUpdateLogoSuccess: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.updateLogo.success = action.payload
  },
  resetUpdateLogoState: (state: DeveloperSliceType) => {
    state.updateLogo = initialState.updateLogo
  },

  /* Offset By Excel Reducers*/
  setOffsetByExcelStep: (state: DeveloperSliceType, action: PayloadAction<number>) => {
    state.offsetByExcel.step = action.payload
  },
  setOffsetOptionalFields: (state: DeveloperSliceType, action: PayloadAction<OffsetOptionalFieldsType>) => {
    state.offsetByExcel.optionalFields = action.payload
  },
  setOffsetOptionalField: (state: DeveloperSliceType, action: PayloadAction<{ key: string, value: string}>) => {
    state.offsetByExcel.offsetOptionalKeys[String(action.payload.key)] = action.payload.value
  },
  resetOffsetByExcelState: (state: DeveloperSliceType) => {
    state.offsetByExcel = initialState.offsetByExcel
  },
  setOffsetByExcelKeysAndExcelData: (
    state: DeveloperSliceType, action: PayloadAction<{keys: string[], excelData: any[]}>
  ) => {
    const { keys, excelData } = action.payload
    state.offsetByExcel.keys = keys
    state.offsetByExcel.excelData = excelData
  },
  resetOffsetByExcelKeysAndExcelData: (state: DeveloperSliceType) => {
    state.offsetByExcel.keys = []
    state.offsetByExcel.excelData = []
  },
  setOffsetByExcelKeysSelected: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.offsetByExcel.offsetKeys = action.payload
  },
  setPreparePurchasesLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.offsetByExcel.preparePurchases.loading = action.payload
  },
  setPreparePurchasesData: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    state.offsetByExcel.preparePurchases.data = action.payload
  },
  setSelectedProjectForPurchase: (state: DeveloperSliceType, action: PayloadAction<number>) => {
    state.offsetByExcel.preparePurchases.selectedProjectId = action.payload
  },
  setSelectedVintagetForPurchase: (state: DeveloperSliceType, action: PayloadAction<number>) => {
    state.offsetByExcel.preparePurchases.selectedVintageYear = action.payload
  },
  setOffsetPurchasesLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.offsetByExcel.makePurchases.loading = action.payload
  },
  setOffsetPurchasesHasFinished: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.offsetByExcel.makePurchases.hasFinished = action.payload
  },
  setOffsetPurchasesPurchasesResults: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    const oldValues = state.offsetByExcel.makePurchases.purchasesResults
    state.offsetByExcel.makePurchases.purchasesResults = [...oldValues, action.payload]
  },
  setOffsetPurchasesFailedResults: (state: DeveloperSliceType, action: PayloadAction<any>) => {
    const oldValues = state.offsetByExcel.makePurchases.failedPurchases
    state.offsetByExcel.makePurchases.failedPurchases = [...oldValues, action.payload]
  },
  resetOffsetPurchases: (state: DeveloperSliceType) => {
    state.offsetByExcel.makePurchases = initialState.offsetByExcel.makePurchases
  },
  // All Projects Integration Reducers
  setAllProjectsIntegrationLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.allProjects.loading = action.payload
  },
  setAllProjectsIntegrationData: (
    state: DeveloperSliceType, action: PayloadAction<SmallProjectIntegration[]>
  ) => {
    state.allProjects.data = action.payload
  },
  resetAllProjectsIntegration: (state: DeveloperSliceType) => {
    state.allProjects = initialState.allProjects
  },
  // Create Deposit Reducers
  setCreateDepositLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.createDeposit.loading = action.payload
  },
  setCreateDepositData: (state: DeveloperSliceType, action: PayloadAction<CreditCardOrders>) => {
    state.createDeposit.data = action.payload
  },
  setCreateDepositError: (state: DeveloperSliceType, action: PayloadAction<string>) => {
    state.createDeposit.error = action.payload
  },
  resetCreateDeposit: (state: DeveloperSliceType) => {
    state.createDeposit = initialState.createDeposit
  },

  // PrePurchase Reducers
  setPrePurchaseLoading: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.prePurchase.loading = action.payload
  },
  setPrePurchaseSuccess: (state: DeveloperSliceType, action: PayloadAction<boolean>) => {
    state.prePurchase.success = action.payload
  },
  setPrePurchaseOrderId: (state: DeveloperSliceType, action: PayloadAction<number>) => {
    state.prePurchase.orderId = action.payload
  },
  setPrePurchaseError: (state: DeveloperSliceType, action: PayloadAction<string>) => {
    state.prePurchase.error = action.payload
  },
  resetPrePurchase: (state: DeveloperSliceType) => {
    state.prePurchase = initialState.prePurchase
  },
  setFinalUnitPrice: (state: DeveloperSliceType, action: PayloadAction<FinalUnitPrice>) => {
    state.final_unit_price = action.payload
  },
}

export { reducers }
